/** Advertising-Agency (L2) affiliations picker.
 *
 *  Renders a categorised list of ad-industry affiliations with a checkbox
 *  and an optional membership-number input for each. Whether the number
 *  is optional or required is driven by the backend (env flag
 *  `ADV_AFFILIATIONS_MEMBERSHIP_REQUIRED`) so we can flip the rule later
 *  without a frontend release.
 *
 *  Emits a normalized list of `[{code, membership_number}, …]` upstream
 *  via `onChange`.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { CheckSquare, Square, Handshake } from "lucide-react";

import { getBackendUrl } from "../lib/backend-url";
const API = `${getBackendUrl()}/api`;

export default function AdvertisingAffiliationsPicker({
  value,             // [{code, membership_number}]
  onChange,
  minRequired = 0,   // enforce "select at least N" — 0 = fully optional
  testidPrefix = "adv-affiliations",
}) {
  const [groups, setGroups] = useState([]);
  const [required, setRequired] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/meta/advertising-affiliations`)
      .then(({ data }) => {
        setGroups(data.groups || []);
        setRequired(Boolean(data.membership_required));
      })
      .catch((err) => console.warn("[AdvAffiliations] catalogue fetch failed", err))
      .finally(() => setLoading(false));
  }, []);

  const rows = Array.isArray(value) ? value : [];
  const byCode = Object.fromEntries(rows.map((r) => [r.code, r]));
  const isChecked = (code) => Boolean(byCode[code]);

  const toggle = (code) => {
    if (isChecked(code)) {
      onChange(rows.filter((r) => r.code !== code));
    } else {
      onChange([...rows, { code, membership_number: "" }]);
    }
  };

  const setMembership = (code, membership_number) => {
    onChange(rows.map((r) => (r.code === code ? { ...r, membership_number } : r)));
  };

  if (loading) {
    return (
      <div className="mt-4 text-[11px] text-white/50 tracking-[0.2em] uppercase"
           data-testid={`${testidPrefix}-loading`}>
        Loading affiliations…
      </div>
    );
  }

  if (!groups.length) return null;

  const checkedCount = rows.length;
  const meetsMin     = checkedCount >= minRequired;

  // Section-level requirement label. Membership-number requirement is on
  // the sub-line (driven by `required`).
  let sectionSuffix;
  if (minRequired > 0 && !meetsMin) {
    sectionSuffix = <span className="text-amber-300">· Select at least {minRequired}</span>;
  } else if (minRequired > 0 && meetsMin) {
    sectionSuffix = <span className="text-emerald-300">· Selected</span>;
  } else {
    sectionSuffix = <span className="text-white/40">· Optional</span>;
  }

  return (
    <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] p-5"
         data-testid={`${testidPrefix}-block`}>
      <div className="flex items-start gap-3">
        <Handshake size={16} className="text-amber-400 mt-0.5 shrink-0" />
        <div className="flex-1">
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/70">
            Industry Affiliations {sectionSuffix}
          </div>
          <p className="text-[11px] text-white/50 mt-1 leading-relaxed max-w-xl">
            {minRequired > 0
              ? `Tick every trade body, watchdog, festival or council you're affiliated with. You must select at least ${minRequired} to continue.`
              : "Tick every trade body, watchdog, festival or council you're affiliated with."}
            {" "}Membership number is {required ? "required" : "optional for now"}.
          </p>
        </div>
        <div className={`text-[10px] tracking-[0.22em] uppercase ${
              meetsMin ? "text-emerald-300/80" : "text-amber-300/80"
            }`}
             data-testid={`${testidPrefix}-count`}>
          {checkedCount} selected
        </div>
      </div>

      <div className="mt-5 space-y-5">
        {groups.map((g) => (
          <div key={g.category} data-testid={`${testidPrefix}-group-${g.category.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}>
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 mb-2">
              {g.category}
            </div>
            <div className="grid gap-2">
              {g.items.map((it) => {
                const checked = isChecked(it.code);
                const Icon = checked ? CheckSquare : Square;
                return (
                  <div key={it.code}
                       data-testid={`${testidPrefix}-row-${it.code}`}
                       className={`rounded-xl border px-3 py-2.5 transition-colors ${
                         checked
                           ? "border-emerald-500/40 bg-emerald-500/[0.06]"
                           : "border-white/10 hover:border-white/25"
                       }`}>
                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        type="button"
                        onClick={() => toggle(it.code)}
                        data-testid={`${testidPrefix}-toggle-${it.code}`}
                        className="flex items-center gap-2 text-left flex-1 min-w-0"
                      >
                        <Icon size={16}
                              className={checked ? "text-emerald-400" : "text-white/50"} />
                        <div className="min-w-0">
                          <div className="text-sm text-white/90 truncate">
                            <span className="font-semibold">{it.name}</span>
                            <span className="text-white/40"> — {it.full_name}</span>
                          </div>
                        </div>
                      </button>
                      {checked && (
                        <input
                          type="text"
                          value={byCode[it.code].membership_number || ""}
                          onChange={(e) => setMembership(it.code, e.target.value)}
                          placeholder={required ? "Membership # (required)" : "Membership # (optional)"}
                          className={`h-9 rounded-lg bg-black/40 border px-3 text-xs w-56 ${
                            required && !byCode[it.code].membership_number
                              ? "border-red-500/40"
                              : "border-white/15"
                          }`}
                          data-testid={`${testidPrefix}-membership-${it.code}`}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
